import React, { PropsWithChildren, forwardRef } from "react";
import { DEFAULTS } from "../utils/constants/defaults";
import { AppTheme } from "../utils/constants/theme";

export type BackgroundColorProps =
  | BackgroundRegularProps
  | BackgroundGradientProps;

export type BackgroundRegularProps = {
  type: "regular";
  color: string;
};

export type BackgroundGradientProps = {
  type: "gradient";
  direction: "to top" | "to right" | "to bottom" | "to left" | number;
  color: string[];
};

export type ShadowSizes = "sm" | "md" | "lg";
export type ShadowSizesValue = {
  blur: number;
  spread: number;
};

export type ContentSpacingSize = "none" | "sm" | "md" | "lg";

export type IndentionSpacingCoords = {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
};

export type ContentSpacingCoords = {
  x: ContentSpacingSize;
  y: ContentSpacingSize;
};

export type ContainerProps = PropsWithChildren &
  React.HTMLAttributes<HTMLDivElement> & {
    indention?: IndentionSpacingCoords | number;
    style?: React.CSSProperties;
    bordered?: boolean;
    borderwidth?: number;
    borderColor?: string;
    cornerRadius?: number;
    flexDisplay?: boolean;
    flexDimension?: boolean;
    contentAlign?: "center" | "start" | "end" | "between" | "around";
    itemsAlign?: "start" | "center" | "end";
    direction?: "row" | "column";
    contentSpacing?: Partial<ContentSpacingCoords>;
    gap?: number;
    stretch?: boolean;
    minWFull?: boolean;
    minHFull?: boolean;
    wFull?: boolean;
    hFull?: boolean;
    backgroundColor?: BackgroundColorProps;
    shadow?: ShadowSizes;
    position?: "relative" | "absolute" | "fixed";
    wrapContent?: boolean;
    id?: string;
  };

const shadowValue: Record<ShadowSizes, ShadowSizesValue> = {
  sm: { blur: DEFAULTS.shadowBlurSm, spread: DEFAULTS.shadowSpreadSm },
  md: { blur: DEFAULTS.shadowBlurMd, spread: DEFAULTS.shadowSpreadMd },
  lg: { blur: DEFAULTS.shadowBlurLg, spread: DEFAULTS.shadowSpreadLg },
};

const Container = forwardRef<HTMLDivElement, ContainerProps>(
  (
    {
      children,
      indention = 5,
      style,
      borderColor,
      cornerRadius = 5,
      bordered,
      borderwidth = 1,
      flexDisplay = false,
      contentAlign = "start",
      itemsAlign = "start",
      direction = "column",
      contentSpacing = { x: "none", y: "none" },
      gap,
      stretch,
      minWFull,
      minHFull,
      hFull,
      wFull,
      position = "relative",
      backgroundColor = { type: "regular", color: AppTheme.transparent },
      shadow,
      wrapContent,
      flexDimension,
      className,
      id,
      ...rest
    },
    ref
  ) => {
    const isCenterContent = contentAlign === "center";
    const isBetweenContent = contentAlign === "between";
    const isEndContent = contentAlign === "end";
    const isAroundContent = contentAlign === "around";

    const isItemsCenter = itemsAlign === "center";
    const isItemsEnd = itemsAlign === "end";

    return (
      <div
        id={id}
        {...rest}
        ref={ref}
        style={{
          ...(!!indention && typeof indention === "number"
            ? { padding: `${indention}px` }
            : typeof indention === "object"
            ? {
                padding: `${indention.top || 0}px ${indention.right || 0}px ${
                  indention.bottom || 0
                }px ${indention.left || 0}px`,
              }
            : {}),
          ...(bordered && {
            border: `${borderwidth}px solid ${
              borderColor || DEFAULTS.borderColor
            }`,
          }),
          ...(!!contentAlign && {
            ...(isCenterContent
              ? { justifyContent: "center" }
              : isEndContent
              ? { justifyContent: "end" }
              : isBetweenContent
              ? { justifyContent: "space-between" }
              : isAroundContent
              ? { justifyContent: "space-around" }
              : { justifyContent: "start" }),
          }),
          ...(isItemsCenter
            ? { alignItems: "center" }
            : isItemsEnd
            ? { alignItems: "end" }
            : { alignItems: "start" }),
          ...(backgroundColor.type === "regular"
            ? {
                backgroundColor: backgroundColor.color,
              }
            : {
                background: `linear-gradient(${
                  typeof backgroundColor.direction === "number"
                    ? `${backgroundColor.direction}deg`
                    : backgroundColor.direction
                }, ${backgroundColor.color.join(", ")})`,
              }),
          ...(!!shadow && {
            boxShadow: `0px 0px ${shadowValue[shadow].blur}px ${shadowValue[shadow].spread}px ${DEFAULTS.shadowColor}`,
          }),
          ...(flexDisplay && {
            display: "flex",
            flexDirection: direction,
            ...(gap && { gap }),
          }),
          ...(wrapContent && { flexWrap: "wrap" }),
          borderRadius: `${cornerRadius}px`,
          position,
          ...(flexDimension && { flex: 1 }),
          ...style,
        }}
        className={`
          ${hFull ? "h-[100%]" : ""}
          ${wFull ? "w-[100%]" : ""}
          ${minHFull ? "min-h-full" : ""}
          ${minWFull ? "min-w-full" : ""}
          ${stretch ? "h-fit w-fit" : ""}
          ${contentSpacing.x === "sm" ? "space-x-[var(--content-spacing-sm)]" : ""}
          ${contentSpacing.x === "md" ? "space-x-[var(--content-spacing-md)]" : ""}
          ${contentSpacing.x === "lg" ? "space-x-[var(--content-spacing-lg)]" : ""}
          ${contentSpacing.y === "sm" ? "space-y-[var(--content-spacing-sm)]" : ""}
          ${contentSpacing.y === "md" ? "space-y-[var(--content-spacing-md)]" : ""}
          ${contentSpacing.y === "lg" ? "space-y-[var(--content-spacing-lg)]" : ""}
          ${className || ""}
        `}
      >
        {children}
      </div>
    );
  }
);

Container.displayName = "Container";

export default Container;
